
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import DateUtil from '@tekvue/assets/utils/DateUtil';
import { VinculoMesa, StatusType } from '../types-mesa';
import { hoursInIntervals } from './utils-mesa';

@Component
export default class TabelaMesa extends Vue {
  @Prop({ default: () => DateUtil.getCurrentDate() }) readonly date!: string;
  @Prop({ required: true }) readonly hours!: string[];
  @Prop({ required: true }) readonly rows!: VinculoMesa[];
  @Prop({ default: false }) readonly showDetails!: boolean;

  clickedRows: string[] = [];
  rowHighlightStates: Record<string, number> = {};
  status: Record<string, Record<string, StatusType>> = {};

  @Watch('hours', { immediate: true, deep: true })
  @Watch('rows', { immediate: true, deep: true })
  updateStatus() {
    const newStatus: Record<string, Record<string, StatusType>> = {};

    this.rows.forEach(vinculo => {
      const workedHours = new Set(hoursInIntervals(vinculo.horas_trabalhadas));
      const scheduledHours = new Set(hoursInIntervals(vinculo.horas_escala));

      newStatus[vinculo.NRVINCULOM] = {};

      this.hours.forEach(hour => {
        if (workedHours.has(hour)) {
          newStatus[vinculo.NRVINCULOM][hour] = 'worked';
        } else if (scheduledHours.has(hour)) {
          newStatus[vinculo.NRVINCULOM][hour] = 'missed';
        } else {
          newStatus[vinculo.NRVINCULOM][hour] = 'none';
        }
      });
    });

    // Detect changes and apply glow animation
    Object.keys(newStatus).forEach(nrVinculo => {
      Object.keys(newStatus[nrVinculo]).forEach(hour => {
        const oldHourStatus = this.status[nrVinculo] && this.status[nrVinculo][hour];
        const newHourStatus = newStatus[nrVinculo][hour];

        if (oldHourStatus !== newHourStatus) {
          this.triggerGlowAnimation(nrVinculo, hour);
        }
      });
    });

    this.status = newStatus;
  }

  triggerGlowAnimation(nrVinculo: string, hour: string) {
    const element = this.$el.querySelector(
      `[data-vinculo-id="${nrVinculo}"][data-hour="${hour}"]`
    );
    if (element) {
      element.classList.add('glow');
      setTimeout(() => element.classList.remove('glow'), 1000);
    }
  }

  get estruturasAgrupadas() {
    const grupos: Record<string, { NRESTRUTGEREN: string, NMESTRUTGEREN: string, rows: VinculoMesa[] }> = {}

    // Sort rows by name before grouping
    const sortedRows = [...this.rows].sort((a, b) => a.NMVINCULOM.localeCompare(b.NMVINCULOM));

    sortedRows.forEach(v => {
      if (!grupos[v.NRESTRUTGEREN]) {
        grupos[v.NRESTRUTGEREN] = {
          NRESTRUTGEREN: v.NRESTRUTGEREN,
          NMESTRUTGEREN: v.NMESTRUTGEREN,
          rows: []
        }
      }
      grupos[v.NRESTRUTGEREN].rows.push(v)
    })

    return Object.values(grupos)
  }

  get isCurrentDate(): boolean {
    return this.date === DateUtil.getCurrentDate();
  }

  isCurrentHour(hora: string): boolean {
    const currentHour = new Date().getHours();
    return parseInt(hora) === currentHour && this.isCurrentDate;
  }

  cycleRowHighlight(vinculo: VinculoMesa, event: MouseEvent): void {
    if (event.ctrlKey) {
      Vue.set(this.rowHighlightStates, vinculo.NRVINCULOM, 0); // Remove highlight
    } else {
      const currentState = this.rowHighlightStates[vinculo.NRVINCULOM] || 0;
      Vue.set(this.rowHighlightStates, vinculo.NRVINCULOM, (currentState + 1) % 5); // Ensure reactivity
    }
  }

  getRowClass(vinculo: VinculoMesa): Record<string, boolean> {
    const state = this.rowHighlightStates[vinculo.NRVINCULOM] || 0;
    return {
      'highlight-yellow': state === 1,
      'highlight-pink': state === 2,
      'highlight-purple': state === 3,
      'highlight-blue': state === 4,
      'hightlight': state > 0,
    };
  }

  isRowHighlighted(vinculo: VinculoMesa): boolean {
    return (this.rowHighlightStates[vinculo.NRVINCULOM] || 0) > 0;
  }

  getStatusColor(status: StatusType): string {
    switch (status) {
      case 'worked':
        return 'green';
      case 'missed':
        return 'red';
      case 'none':
        return 'grey lighten-2';
      default:
        return 'grey lighten-2';
    }
  }

  getStatusIcon(status: StatusType): string {
    switch (status) {
      case 'worked':
        return '\u2714';
      case 'missed':
        return '\u2716';
      case 'none':
        return '';
      default:
        return '';
    }
  }

  mounted() {
    const savedStates = localStorage.getItem('rowHighlightStates');
    if (savedStates) {
      this.rowHighlightStates = JSON.parse(savedStates);
    }
  }

  @Watch('rowHighlightStates', { deep: true })
  onRowHighlightStatesChange() {
    localStorage.setItem('rowHighlightStates', JSON.stringify(this.rowHighlightStates));
  }
}
